import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import styles from './styles.module.scss';
import { ErrorCard } from '@starsoft/common/components';
import UserEmailFormSkeleton from '../Skeleton';

import { UserEmailVerifyInputDto } from '@starsoft/common/dto';
import { UserEmailFormProps } from '../props';
import { useUserEmailsForm } from '@starsoft/common/hooks/user-emails/useUserEmailsForm';
import { PropsWithChildren } from 'react';

/**
 * UserEmailFormLayout Component
 * This component is responsible for rendering the layout of the user email form.
 * It manages the form state using react-hook-form and handles different states like loading, error, and skeleton.
 *
 * @param {PropsWithChildren<UserEmailFormProps>} props - The properties for the component.
 * @param {boolean} props.useMinimalForm - Indicates if the minimal form should be used.
 * @param {React.ReactNode} props.children - The child components to be rendered inside the form.
 *
 * @returns {JSX.Element} The rendered form layout component.
 *
 * @example
 * // Usage example:
 * <UserEmailFormLayout useMinimalForm={false}>
 *   <YourFormComponents />
 * </UserEmailFormLayout>
 */
export default function UserEmailFormLayout({
  useMinimalForm,
  children,
}: PropsWithChildren<UserEmailFormProps>) {
  // Initialize form methods using react-hook-form
  const methods: UseFormReturn<UserEmailVerifyInputDto> =
    useForm<UserEmailVerifyInputDto>();

  // Destructure handleSubmit from form methods
  const { handleSubmit } = methods ?? {};

  // Use custom hook to manage form state and actions
  const { isSkeleton, error, onSubmit, onError, handleError, isLoadingEmails } =
    useUserEmailsForm({ methods, useMinimalForm });

  // Render error state with an ErrorCard component
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.container__content}>
          <ErrorCard error={error} refetch={handleError} />
        </div>
      </div>
    );
  }

  // Render skeleton state or loading state with a UserEmailFormSkeleton component
  if (isSkeleton || isLoadingEmails) {
    return <UserEmailFormSkeleton />;
  }

  // Render the form with FormProvider and handle form submission
  return (
    <FormProvider {...methods}>
      <form
        className={styles.container}
        onSubmit={handleSubmit(onSubmit, onError)}
      >
        {children}
      </form>
    </FormProvider>
  );
}
